import { RiAtLine } from '@remixicon/react';
import Mention from '@tiptap/extension-mention';
import TextAlign from '@tiptap/extension-text-align';
import { BackgroundColor, FontSize, TextStyle, } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import Modal from '../Modal.js';
import { DisableEnter } from './extentions.js';
import { Marks } from './Marks.js';
import MentionExplanation from './MentionExplanation.js';
import suggestion from './suggestion.js';
const Editor = (props) => {
    const explanationLabel = useSelector((s) => s.lang.interactions.explanation);
    const mentionsRef = useRef(props.mentions);
    const [mentionHelpOpen, setMentionHelpOpen] = useState(false);
    const hasMentionHelp = (props.mentionSuggestions?.length ?? 0) > 0;
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                code: false,
                codeBlock: false,
            }),
            Placeholder.configure({
                placeholder: props.placeholder,
            }),
            Mention.configure({
                HTMLAttributes: {
                    class: 'mention',
                },
                suggestion: suggestion(mentionsRef),
                renderText: ({ options, node, suggestion }) => {
                    return `${suggestion?.char}${node.attrs.label}`;
                },
            }),
            TextAlign.configure({
                types: ['paragraph', 'heading'],
            }),
            TextStyle,
            FontSize,
            BackgroundColor,
            ...(props.singleLine ? [DisableEnter] : []),
        ],
        // If initialContent changes, current state will be set to the new initialContent
        content: props.initialContent ?? '<p></p>',
        editable: !props.disabled,
        onUpdate: ({ editor }) => {
            // TODO: An onChangePlainText to listen to editor.getText())
            if (props.onChange != null) {
                // TODO: Possibly debounce (did not seem to be an issue in my testing, but possible in the future)
                const html = editor.getHTML();
                props.onChange(html);
            }
        },
        onFocus: () => {
            props.onFocus?.();
        },
        onBlur: () => {
            props.onBlur?.();
        },
    });
    useEffect(() => {
        if (props.initialContent != null) {
            editor.chain().setContent(props.initialContent).run();
            if (props.onChange != null) {
                props.onChange(editor.getHTML());
            }
        }
    }, [props.initialContent]);
    useEffect(() => {
        mentionsRef.current = props.mentions;
    }, [props.mentions]);
    useEffect(() => {
        editor?.setEditable(!props.disabled);
    }, [props.disabled]);
    return (React.createElement(Wrapper, { compact: props.compact ?? false, disabled: props.disabled ?? false },
        !props.disabled &&
            (props.hideControls != null ? !props.hideControls : true) ? (React.createElement(Marks, { editor: editor })) : null,
        !props.disabled && hasMentionHelp && (React.createElement(MentionHelpButton, { type: "button", onClick: () => setMentionHelpOpen(true), title: explanationLabel, "aria-label": explanationLabel },
            React.createElement(RiAtLine, { size: 14 }))),
        React.createElement(StyledEditorContent, { compact: props.compact ?? false, minHeight: props.minHeight, onClick: () => {
                if (!props.disabled) {
                    editor.chain().focus().run();
                }
            }, editor: editor }),
        mentionHelpOpen && props.mentionSuggestions && (React.createElement(Modal, { onToggle: () => setMentionHelpOpen(false), header: explanationLabel },
            React.createElement(MentionExplanation, { suggestions: props.mentionSuggestions })))));
};
const MentionHelpButton = styled.button `
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid ${THEME.BORDER};
  background: white;
  color: ${THEME.COLOR_GREY};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    color: ${THEME.COLOR_ACCENT};
    border-color: ${THEME.ACCENT_BORDER};
    background: ${THEME.ACCENT_SURFACE};
  }

  &:focus-visible {
    outline: 2px solid ${THEME.COLOR_ACCENT};
    outline-offset: 1px;
  }
`;
const Wrapper = styled.div `
  position: relative;
  border: 1px solid ${THEME.BORDER_STRONG};
  border-radius: 3px;
  background: ${(props) => (props.disabled ? THEME.SURFACE_MUTED : 'white')};
  ${(props) => props.disabled
    ? `
    opacity: 0.7;
    cursor: not-allowed;
  `
    : ''}

  /* Remove outline */
  .ProseMirror:focus {
    outline: none;
  }

  .tiptap p.is-editor-empty:first-child::before {
    color: ${THEME.TEXT_SUBTLE};
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }

  .mention {
    background: #d5e2ff;
    border-radius: 3px;
    padding: 1px 3px;
  }

  h1,
  h2,
  h3,
  p {
    ${(props) => (props.compact ? 'margin: 0;' : '')}
  }
`;
const StyledEditorContent = styled(EditorContent) `
  padding: 10px;
  min-height: ${(props) => props.minHeight ?? (props.compact ? '0' : '200px')};
`;
export default Editor;
//# sourceMappingURL=Editor.js.map